import { useState } from "react";
import { t } from "i18next";
import { cn } from "@edusogno/lib/utils";
import { useMe } from "@/data/user";
import { useMore } from "@/contexts/MoreContext";
import { getMoment } from "@/utils/agenda.ts";
import { useAgenda } from "@/contexts/AgendaContext.tsx";
import { LoginUserItem } from "@/types";
import {
  maixpanelTrackEventProps,
  MIXPANEL_PAGE_VERSION_DESKTOP,
} from "@/mixpanel/MixpanelUtils";
import dayjs from "dayjs";
import Button from "@edusogno/primitives/button";
import isoWeek from "dayjs/plugin/isoWeek";
import WeekView from "@/components/agenda/desktop/WeekView.tsx";
import MonthView from "@/components/agenda/desktop/MonthView.tsx";
import weekOfYear from "dayjs/plugin/weekOfYear";
import ChevronLeft from "@/components/icons/ChevronLeft.tsx";
import ChevronRight from "@/components/icons/ChevronRight.tsx";
import advancedFormat from "dayjs/plugin/advancedFormat";
import BookSpeakingClass from "./book-speaking-class/SpeakingClassPopup";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
// TIMEZONE: TURNED OFF FOR NOW
// import moment from "moment-timezone";
// import { useTimezone } from "@/contexts/TimezoneContext";

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(advancedFormat);

const DesktopAgendaView = () => {
  const { me } = useMe();
  const { isBeta } = useMore();

  const {
    next,
    week,
    index,
    today,
    nextWeek,
    previous,
    isMonthView,
    previousWeek,
    isCurrentWeek,
    toggleMonthView,
  } = useAgenda();

  const [openSpeakingClass, setOpenSpeakingClass] = useState(false);
  // const { selectedTimezone } = useTimezone();
  // TIMEZONE: TURNED OFF FOR NOW
  // const userToken = me?.item?.user?.token || "default-token";
  // const [searchQuery, setSearchQuery] = useState("");
  // const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // const [showConfirmation, setShowConfirmation] = useState(false);
  // const [previousTimezone, setPreviousTimezone] = useState(selectedTimezone);

  // const { mutate: updateTimezone, isLoading } = useUpdateTimezone();

  // const filteredTimezones = moment.tz
  //   .names()
  //   .filter((tz) => tz.toLowerCase().includes(searchQuery.toLowerCase()));

  // mixpanel tracking
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Agenda", me?.item?.user as LoginUserItem),
  );

  const handleOpenSpeakingClass = () => {
    setOpenSpeakingClass(true);
  };

  const handleCloseSpeakingClass = () => {
    setOpenSpeakingClass(false);
  };

  // TIMEZONE: TURNED OFF FOR NOW

  // const handleTimezoneChange = (tz: string) => {
  //   setPreviousTimezone(selectedTimezone);
  //   setSelectedTimezone(tz);
  //   setShowConfirmation(true);
  //   setIsDropdownOpen(false);
  // };

  // const confirmTimezoneChange = () => {
  //   updateTimezone(
  //     { token: userToken, timezone: selectedTimezone },
  //     {
  //       onSuccess: () => {
  //         setSelectedTimezone(selectedTimezone);
  //       },
  //       onError: (error) => {
  //         console.error("Failed to update timezone:", error);
  //       },
  //     },
  //   );
  //   setShowConfirmation(false);
  // };

  // const cancelTimezoneChange = () => {
  //   setShowConfirmation(false);
  //   setSelectedTimezone(previousTimezone);
  // };

  // const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setSearchQuery(event.target.value);
  // };

  // const toggleDropdown = () => {
  //   setIsDropdownOpen(!isDropdownOpen);
  // };

  return (
    <DashboardContentLayout>
      <div className="flex flex-row justify-between h-[70px]">
        <div className="relative flex w-full flex-row items-start gap-4 h-[31px]">
          <Button
            data-track-props={mixpanelPropsData}
            data-track-pageversion={MIXPANEL_PAGE_VERSION_DESKTOP}
            disabled={isCurrentWeek}
            className={cn(
              "flex items-center !opacity-100 justify-center text-sm w-[101px] font-medium ",
              {
                "bg-[#56438A] !text-white": isCurrentWeek,
                "[--bg--primary] !text-white": !isCurrentWeek,
              },
            )}
            onClick={() => today()}
            size={"sm"}
          >
            {t("agenda.today")}
          </Button>

          <div className="flex flex-row items-center h-full gap-4">
            <button
              onClick={() => (isMonthView ? previous() : previousWeek())}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  if (isMonthView) {
                    previous();
                  } else {
                    previousWeek();
                  }
                }
              }}
            >
              <ChevronLeft className="w-5 h-5 stroke-white" />
            </button>

            <button
              onClick={() => (isMonthView ? next() : nextWeek())}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  if (isMonthView) {
                    next();
                  } else {
                    nextWeek();
                  }
                }
              }}
            >
              <ChevronRight className="w-5 h-5 stroke-white" />
            </button>

            <h3 className="text-2xl capitalize items-center leading-[99%] font-semibold text-light">
              {isMonthView ? (
                <>
                  {getMoment(index).format("MMMM")}&nbsp;
                  {getMoment(index).format("YYYY")}
                </>
              ) : (
                <>
                  {`${t("agenda.calendar_initial")} ${dayjs(
                    week.at(-1),
                  ).isoWeek()}: ${dayjs(week[0])
                    .format("DD  MMM")
                    .split(".")
                    .join("")} - ${dayjs(week.at(-1))
                    .format("DD  MMM")
                    .split(".")
                    .join("")}`}
                </>
              )}
            </h3>
          </div>
        </div>

        <div className="flex flex-row gap-x-3 h-[31px] items-center">
          {isBeta && (
            <button
              color="primary"
              onClick={handleOpenSpeakingClass}
              className={cn(
                "bg-primary text-white font-semibold px-2 whitespace-nowrap w-full h-[35px] rounded-[10px]",
              )}
            >
              {t("btns.speaking_class")}
            </button>
          )}

          <Button
            id="agendaView"
            color="primary"
            size="lg"
            onClick={toggleMonthView}
            className="flex items-center justify-center text-sm px-2 whitespace-nowrap font-medium [--bg:--primary100] [--color:--secondary]"
          >
            {isMonthView ? t("agenda.week_view") : t("agenda.month_view")}
          </Button>

          <BookSpeakingClass
            open={openSpeakingClass}
            onClose={handleCloseSpeakingClass}
          />
        </div>
      </div>
      <div className={"block h-[--content-height] max-h-[--content-height]"}>
        {isMonthView ? <MonthView /> : <WeekView />}

        {/* TIMEZONE: TURNED OFF FOR NOW */}
        {/* <div className={`w-fit ${isMonthView ? "mt-[4px]" : "my-2"}`}>
          <div className="flex items-center space-x-2">
            <div>
              <label
                htmlFor="timezone-select"
                className="text-white text-sm leading-[14px]"
              >
                Select Timezone:
              </label>
            </div>
            <div className="relative">
              <button
                id="timezone-select"
                onClick={toggleDropdown}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    toggleDropdown();
                  }
                }}
                className="w-full p-1.5 rounded text-sm leading-[14px] text-black bg-white cursor-pointer"
                aria-expanded={isDropdownOpen}
                aria-controls="timezone-dropdown"
              >
                {selectedTimezone}
              </button>
              {isDropdownOpen && (
                <div
                  id="timezone-dropdown"
                  className="absolute bottom-full mb-2 w-64 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto z-50"
                >
                  <input
                    id="timezone-search"
                    type="text"
                    placeholder="Search timezone..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full p-2 border-b border-gray-300 focus:outline-none"
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {filteredTimezones.map((tz) => (
                      <button
                        key={tz}
                        onClick={() => handleTimezoneChange(tz)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleTimezoneChange(tz);
                          }
                        }}
                        className="w-full text-left p-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {tz}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isLoading && (
                <p className="text-sm text-gray-500">Updating timezone...</p>
              )}
            </div>
          </div>
        </div> */}
      </div>

      {/* TIMEZONE: TURNED OFF FOR NOW */}
      {/* {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="bg-white p-6 rounded-lg shadow-lg"
            role="dialog"
            aria-labelledby="confirmation-title"
          >
            <p id="confirmation-title" className="mb-4">
              Do you want to change your timezone to {selectedTimezone}?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelTimezoneChange}
                className="bg-gray-300 text-black px-4 py-2 rounded"
              >
                No
              </button>
              <button
                onClick={confirmTimezoneChange}
                className="bg-primary text-white px-4 py-2 rounded"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )} */}
    </DashboardContentLayout>
  );
};

export default DesktopAgendaView;
